import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ChartLineUp, TrendDown, Coins, Buildings, TreeStructure, Trophy, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import CounterChainTree from "../components/CounterChainTree";

export default function Intelligence() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    const [chains, setChains] = useState([]);
    const [chainStats, setChainStats] = useState({ total_chains: 0, total_offers: 0, avg_rounds: 0, accepted_chains: 0 });
    const [chainsLoading, setChainsLoading] = useState(true);
    const [stateStats, setStateStats] = useState({});
    const [leaderboard, setLeaderboard] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/intelligence/win-rates"),
            api.get("/intelligence/summary"),
        ]).then(([r, s]) => {
            setRows(r.data);
            setSummary(s.data);
        }).catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));

        api.get("/intelligence/counter-chains")
            .then((res) => {
                setChains(res.data.chains || []);
                setChainStats(res.data.stats || {});
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setChainsLoading(false));

        api.get("/intelligence/state-benchmarks")
            .then((res) => setStateStats(res.data.by_state || {}))
            .catch(() => {});

        api.get("/referrals/leaderboard")
            .then((res) => setLeaderboard(res.data))
            .catch(() => {});
    }, []);

    const filtered = rows.filter((r) =>
        !q.trim() || (r.creditor || "").toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="space-y-8" data-testid="intelligence-page">
            <div>
                <p className="overline">Insights</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Settlement intelligence</h1>
                <p className="text-slate-600 mt-3 max-w-2xl">
                    Anonymized, aggregated settlement performance across every LienLift Direct case, plus a complete
                    visual map of every counter-offer on your own cases.
                </p>
                <p className="text-xs text-slate-500 mt-3 italic">
                    Aggregate data is platform-wide, anonymized, and based on cases marked settled with a final settlement amount. Past performance ≠ future results.
                </p>
            </div>

            <Tabs defaultValue="aggregate" className="space-y-6">
                <TabsList data-testid="intel-tabs">
                    <TabsTrigger value="aggregate" data-testid="intel-tab-aggregate">
                        <ChartLineUp size={14} className="mr-1.5" /> Win rates
                    </TabsTrigger>
                    <TabsTrigger value="chains" data-testid="intel-tab-chains">
                        <TreeStructure size={14} className="mr-1.5" /> Counter Offers
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="aggregate" className="space-y-6">
                    {summary && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden" data-testid="intel-summary">
                            <Stat icon={Buildings} label="Settled cases" value={summary.total_settled_cases} tint="blue" />
                            <Stat icon={ChartLineUp} label="Avg settlement %" value={summary.avg_settlement_pct != null ? `${summary.avg_settlement_pct}%` : "—"} tint="green" />
                            <Stat icon={Coins} label="Total settled" value={`$${(summary.total_settled || 0).toLocaleString()}`} tint="brown" />
                            <Stat icon={TrendDown} label="Total savings" value={`$${(summary.total_savings || 0).toLocaleString()}`} tint="red" />
                        </div>
                    )}

                    {Object.keys(stateStats).length > 0 && (
                        <Card className="p-5" data-testid="intel-state-card">
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                <h2 className="font-serif text-xl text-[#0F1E3D]">By state</h2>
                                <span className="text-xs text-slate-500">Anonymized · k≥3 floor</span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                {Object.entries(stateStats)
                                    .sort((a, b) => b[1].settled_count - a[1].settled_count)
                                    .map(([code, s]) => (
                                        <div key={code} className="border border-slate-200 rounded-md p-3" data-testid={`intel-state-${code}`}>
                                            <div className="flex items-center gap-2">
                                                <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-[#0F1E3D] text-white text-[10px] font-mono font-semibold">{code}</span>
                                                <span className="font-mono text-lg font-semibold text-emerald-700">{s.avg_settlement_pct}%</span>
                                            </div>
                                            <p className="text-[11px] text-slate-500 mt-1.5">{s.settled_count} settled · ${s.total_savings.toLocaleString()} saved</p>
                                            <p className="text-[10px] text-slate-400 font-mono mt-0.5">{s.min_settlement_pct}–{s.max_settlement_pct}%</p>
                                        </div>
                                    ))}
                            </div>
                        </Card>
                    )}

                    {leaderboard && leaderboard.leaderboard.length > 0 && (
                        <Card className="p-5" data-testid="intel-leaderboard-card">
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                <div className="flex items-center gap-2">
                                    <Trophy size={20} weight="duotone" className="text-amber-500" />
                                    <h2 className="font-serif text-xl text-[#0F1E3D]">Top referrers</h2>
                                </div>
                                <span className="text-xs text-slate-500">
                                    Lifetime commission · {leaderboard.total_ranked} ranked
                                    {leaderboard.your_rank ? ` · your rank #${leaderboard.your_rank}` : ""}
                                </span>
                            </div>
                            <div className="divide-y divide-slate-100 border border-slate-200 rounded-md overflow-hidden">
                                {leaderboard.leaderboard.map((r) => (
                                    <div
                                        key={r.rank}
                                        className={`flex items-center justify-between px-4 py-3 ${r.is_you ? "bg-emerald-50" : ""}`}
                                        data-testid={`leaderboard-row-${r.rank}`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-md font-mono text-xs font-semibold shrink-0 ${
                                                r.rank === 1 ? "bg-amber-100 text-amber-900" :
                                                r.rank === 2 ? "bg-slate-200 text-slate-700" :
                                                r.rank === 3 ? "bg-orange-100 text-orange-800" :
                                                "bg-slate-100 text-slate-600"
                                            }`}>
                                                {r.rank === 1 ? <Crown size={14} weight="fill" /> : `#${r.rank}`}
                                            </span>
                                            <span className="font-medium text-slate-900 truncate">
                                                {r.display_name}{r.is_you && <span className="ml-1.5 text-xs text-emerald-700 font-semibold">(you)</span>}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 shrink-0">
                                            <span className="text-xs text-slate-500 font-mono hidden sm:inline">{r.conversions} paid</span>
                                            <span className="font-mono text-base font-semibold text-emerald-700">${r.lifetime_commission.toFixed(2)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-3">
                                Names masked for privacy. Share your <a href="/referrals" className="text-[#2563EB] hover:underline">referral link</a> to climb the board.
                            </p>
                        </Card>
                    )}

                    <div className="max-w-md">
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Filter by creditor name…"
                            data-testid="intel-search"
                            className="h-10"
                        />
                    </div>

                    {loading ? (
                        <p className="text-slate-500">Loading…</p>
                    ) : filtered.length === 0 ? (
                        <Card className="p-12 text-center border-dashed">
                            <p className="text-slate-600">
                                {rows.length === 0
                                    ? "No settled cases yet. As cases on the platform are marked Settled with a final settlement amount, this dashboard fills out automatically."
                                    : "No creditors match your filter."}
                            </p>
                        </Card>
                    ) : (
                        <div className="border border-slate-200 rounded-md overflow-hidden">
                            <table className="w-full text-sm" data-testid="intel-table">
                                <thead className="bg-slate-50 text-slate-500">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-medium">Creditor</th>
                                        <th className="text-right px-4 py-3 font-medium">Cases</th>
                                        <th className="text-right px-4 py-3 font-medium">Avg %</th>
                                        <th className="text-right px-4 py-3 font-medium">Range</th>
                                        <th className="text-right px-4 py-3 font-medium">Total owed</th>
                                        <th className="text-right px-4 py-3 font-medium">Total settled</th>
                                        <th className="text-right px-4 py-3 font-medium">Total savings</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((r) => {
                                        const good = r.avg_settlement_pct < 50;
                                        return (
                                            <tr key={r.creditor} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`intel-row-${r.creditor}`}>
                                                <td className="px-4 py-3 font-medium text-slate-900">{r.creditor}</td>
                                                <td className="px-4 py-3 text-right font-mono">{r.settled_count}</td>
                                                <td className={`px-4 py-3 text-right font-mono font-semibold ${good ? "text-emerald-700" : "text-slate-700"}`}>{r.avg_settlement_pct}%</td>
                                                <td className="px-4 py-3 text-right font-mono text-xs text-slate-500">{r.min_settlement_pct}% – {r.max_settlement_pct}%</td>
                                                <td className="px-4 py-3 text-right font-mono">${r.total_owed.toLocaleString()}</td>
                                                <td className="px-4 py-3 text-right font-mono">${r.total_settled.toLocaleString()}</td>
                                                <td className="px-4 py-3 text-right font-mono text-emerald-700">${r.total_savings.toLocaleString()}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="chains" className="space-y-6">
                    <div>
                        <h2 className="font-serif text-2xl">Your Counter Offers</h2>
                        <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
                            Every offer you&apos;ve sent, plus every counter the recipient returned. Each branch is a round of negotiation — the deeper the tree, the more back-and-forth.
                        </p>
                    </div>
                    {chainsLoading ? (
                        <p className="text-slate-500">Loading…</p>
                    ) : (
                        <CounterChainTree chains={chains} stats={chainStats} />
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Stat({ icon: Icon, label, value, tint }) {
    const tones = {
        blue: { bg: "bg-blue-50", icon: "text-blue-700" },
        green: { bg: "bg-emerald-50", icon: "text-emerald-700" },
        red: { bg: "bg-rose-50", icon: "text-rose-700" },
        brown: { bg: "bg-amber-50/80", icon: "text-amber-800" },
    };
    const t = tones[tint] || tones.blue;
    return (
        <div className={`p-6 ${t.bg}`}>
            <Icon size={20} weight="duotone" className={t.icon} />
            <p className="overline mt-3">{label}</p>
            <p className="font-mono text-3xl mt-1 font-semibold">{value}</p>
        </div>
    );
}
